import * as React from "react";
import { Form, FormApi, useFormApi } from "informedv4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { useEffect, useState, useContext } from "react";
import { DateTime } from "../../forms/informedv4/DateTimeV4";
import * as moment from "moment";
import { User, SpirometryAnalysisSet, AnalysisBatch, QueryCondition, OverreadStatusIdEnum, SubjectTypes, QueryOrderDirection, AnalysisSet, Query, DlcoAnalysisSet } from "../../misc/api/apiTypes";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SpirometryAnalysisSetTypeEnum } from "../../misc/api/ApiSpirometryAnalysisSetType";
import { SampleSizeLookup } from "./SampleSizeLookup";
import * as _ from "lodash";
import { LocalQueryTable } from "../../misc/components/queryTable/LocalQueryTable";
import { QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import Modal, { ModalSize } from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DlcoAnalysisSetTypeEnum } from "../../misc/api/ApiDlcoAnalysisSetType";
import { AuditModal, AuditForm } from "../../auditmodal/components/AuditModal";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { PressureAnalysisSetTypeEnum } from "../../misc/api/ApiPressureAnalysisSetTypeEnum";
import { SessionType, QueryOfEcgAnalysisSet, MipAnalysisSet, SnipAnalysisSet, WebPortalClient } from "webportalclient";
import { Dictionary } from "lodash";
import { OscillometryAnalysisSetTypeEnum } from "../../misc/api/ApiOscillometryAnalysisSetTypeEnum";
import { EcgAnalysisSetTypeEnum } from "../../misc/api/ApiEcgAnalysisSetType";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { format, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay } from 'date-fns';

interface AnalysisSetTableProps {
    title: string;
    analysisSets: AnalysisSet[];
    onClose: () => void;
}

const AnalysisSetTable = ({ title, analysisSets, onClose }: AnalysisSetTableProps) => {
    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <h3 className="text-info">{title}</h3>
                    </div>
                    <div className="col-md-12">
                        <LocalQueryTable collection={analysisSets} defaultPageSize={10} rowUniqueIdentifier={row => row.Id} query={{ Data: analysisSets }}>
                            <QueryTableColumn member="site" header="Site" map={row => row.Site.SiteNumber} sortable={false} filterable={false} />
                            <QueryTableColumn member="subject" header="Participant" map={row => row.Subject.SubjectNumber} sortable={false} filterable={false} />
                            <QueryTableColumn member="visit" header="Visit" map={row => row.Visit.Name} sortable={false} filterable={false} />
                            <QueryTableColumn member="session" header="Session" map={row => row.SessionLabel} sortable={false} filterable={false} />
                            <QueryTableColumn member="date" header="Session Start" map={row => <LocalizedDateTime time={row.SessionDateTime} accuracy={DateTimeAccuracy.Second} isTooltip={true} />} sortable={false} filterable={false} />
                        </LocalQueryTable>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={onClose}>Back</button>
            </div>
        </>
    );
}

interface AnalysisBatchesTableProps {
    title: string;
    analysisBatches: AnalysisBatch[];
    currentUser: User;
    onClose: () => void;
    onDelete: () => void;
}

const AnalysisBatchesTable = ({ title, analysisBatches, currentUser, onClose, onDelete }: AnalysisBatchesTableProps) => {

    const [selectedId, setSelectedId] = useState('');
    const [auditVisible, setAuditVisible] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [legacyClient] = useState(useWebPortalLegacyClient());

    const handleDelete = async (id) => {
        setSelectedId(id);
        setAuditVisible(true);
    }

    const handleConfirm = async (values: AuditForm) => {
        setDeleting(true);
        await legacyClient.deleteAnalysisBatch(selectedId, { annotation: values.Annotation });
        handleCloseAudit();
        onDelete();
    }

    const handleCloseAudit = () => {
        setSelectedId('');
        setAuditVisible(false);
        setDeleting(false);
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <h3 className="text-info">{title}</h3>
                    </div>
                    <div className="col-md-12">
                        <LocalQueryTable collection={analysisBatches} defaultPageSize={10} rowUniqueIdentifier={row => row.Id} query={{ Data: analysisBatches }}>
                            <QueryTableColumn member="name" header="Name" map={row => row.Name} sortable={false} filterable={false} />
                            <QueryTableColumn member="sampleSize" header="Samples" map={(row) => row.SampleSize} sortable={false} filterable={false} />
                            <QueryTableColumn member="createdDateTime" header="Created" map={row => <LocalizedDateTime time={row.CreatedDateTime} accuracy={DateTimeAccuracy.Second} isTooltip={true} />} sortable={false} filterable={false} />
                            <QueryTableColumn member="" header="" map={row => analysisBatches[0].Id === row.Id ? (<button className="btn btn-danger" onClick={() => handleDelete(row.Id)}>Delete</button>) : null} sortable={false} filterable={false} />
                        </LocalQueryTable>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={onClose}>Back</button>
            </div>
            {auditVisible ? <AuditModal header="Confirm" isSubmitting={deleting} onSubmit={handleConfirm} onClose={handleCloseAudit} /> : null}
        </>
    );
}

export const CreateAnalysisBatchModalForm = (({ sessionType, minimumTests, currentUser, onClose, onSave }) => {

    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previousBatchesVisible, setPreviousBatchesVisible] = useState(false);
    const [currentBatchVisible, setCurrentBatchVisible] = useState(false);
    const [previousBatchVisible, setPreviousBatchVisible] = useState(false);
    const [interVisible, setInterVisible] = useState(false);
    const [intraVisible, setIntraVisible] = useState(false);
    const [analysts, setAnalysts] = useState<User[]>([]);
    const [analysisBatches, setAnalysisBatches] = useState<AnalysisBatch[]>([]);
    const [interCandidates, setInterCandidates] = useState<AnalysisSet[]>([]);
    const [intraCandidates, setIntraCandidates] = useState<AnalysisSet[]>([]);
    const [interSelected, setInterSelected] = useState<AnalysisSet[]>([]);
    const [intraSelected, setIntraSelected] = useState<AnalysisSet[]>([]);
    const { setValues, getFormState } = useFormApi();
    const [legacyClient] = useState(useWebPortalLegacyClient());

    const getAnalysisSetsByBatch = async (previousBatch) => {
        const legacyQuery = {
            Filters: [
                { Member: "BatchId", Condition: QueryCondition.Equal, Values: [previousBatch] },
                { Member: "BatchId", Condition: QueryCondition.NotEqual, Values: [null] },
            ]
        };

        switch (sessionType) {
            case SessionTypes.Fvc:
                return await legacyClient.getSpirometryAnalysisSets(legacyQuery);
            case SessionTypes.Svc:
                return await legacyClient.getSvcAnalysisSets(legacyQuery);
            case SessionTypes.Dlco:
                return await legacyClient.getDlcoAnalysisSets(legacyQuery);
            case SessionTypes.Mip:
                return await legacyClient.getMipAnalysisSets(legacyQuery);
            case SessionTypes.Mep:
                return await legacyClient.getMepAnalysisSets(legacyQuery);
            case SessionTypes.Snip:
                return await legacyClient.getSnipAnalysisSets(legacyQuery);
            case SessionTypes.Oscillometry:
                return await legacyClient.getOscillometryAnalysisSets(legacyQuery);
            case SessionTypes.Ecg:
                return mapToLegacy(await client.client.getEcgAnalysisSets(undefined, `session/visit/subject/site/studyId eq ${study.id} and batchId eq ${previousBatch} and batchId ne null`, undefined, "session($expand=visit($select=name,subject;$expand=subject($select=id,number,site;$expand=site($select=id,number))))"));
            case SessionTypes.Pef:
                return mapToLegacy(await client.client.getPefAnalysisSets(undefined, `session/visit/subject/site/studyId eq ${study.id} and batchId eq ${previousBatch} and batchId ne null`, undefined, "session($expand=visit($select=name,subject;$expand=subject($select=id,number,site;$expand=site($select=id,number,studyId))))"));
        }
    }

    // TODO: Move this somewhere else will be useful for other areas that use old API
    const mapToLegacy = (query: QueryOfEcgAnalysisSet): Query<AnalysisSet> => {
        const analysisSets = query.value.map(a => ({
            Id: a.id,
            SessionId: a.sessionId,
            StatusId: a.status,
            Status: {
                Id: a.status,
                Name: a.status,
            },
            CreatedDateTime: a.created,
            Study: {
                Name: study.name,
                Id: a.session.visit.subject.site.studyId,
                OldId: 0,
                ProtocolNumber: study.protocol,
                Countries: [],
                SessionTypes: [],
                VisitSchedule: [],
            },
            Site: {
                Id: a.session.visit.subject.siteId,
                SiteNumber: a.session.visit.subject.site.number,
                PrimaryContact: "",
                CountryId: "",
                StudyId: a.session.visit.subject.site.studyId,
            },
            Subject: {
                Id: a.session.visit.subjectId,
                SubjectNumber: a.session.visit.subject.number,
                StudyId: a.session.visit.subject.site.studyId,
                SiteId: a.session.visit.subject.siteId,
                SiteNumber: a.session.visit.subject.site.number,
                SubjectTypeName: a.session.visit.subject.typeName,
            },
            Visit: {
                Id: a.session.visitId,
                Name: a.session.visit.name,
                SubjectId: a.session.visit.subjectId,
                VisitDateTime: a.session.visit.occurred,
                VisitScheduleItemId: a.session.visit.visitScheduleItemId,
            },
            SessionLabel: a.session.name,
            Technician: a.session.technician,
            SessionDateTime: a.session.occurred
        }));

        return {
            Data: analysisSets
        }
    }

    const getAnalysisSets = async (startDateIso: string, endDateIso: string): Promise<Query<AnalysisSet>> => {
        const legacyQuery = {
            Filters: [
                { Member: "LastAcceptabilityChangeDateTime", Condition: QueryCondition.GreaterThan, Values: [startDateIso] },
                { Member: "LastAcceptabilityChangeDateTime", Condition: QueryCondition.LessThanOrEqual, Values: [endDateIso] },
                { Member: "SessionTestCount", Condition: QueryCondition.GreaterThanOrEqual, Values: [minimumTests] },
                { Member: "IsSessionSuppressed", Condition: QueryCondition.Equal, Values: [false] },
                { Member: "Status.Id", Condition: QueryCondition.Equal, Values: [OverreadStatusIdEnum.Accepted, OverreadStatusIdEnum.Rejected] },
                { Member: "Subject.SubjectTypeId", Condition: QueryCondition.Equal, Values: [SubjectTypes.InStudy] },
                { Member: "Type.Id", Condition: QueryCondition.Equal, Values: [analysisSetType] }
            ]
        };

        switch (sessionType) {
            case SessionTypes.Svc:
                return await legacyClient.getSvcAnalysisSets(legacyQuery);
            case SessionTypes.Fvc:
                return await legacyClient.getSpirometryAnalysisSets(legacyQuery);
            case SessionTypes.Dlco:
                return await legacyClient.getDlcoAnalysisSets(legacyQuery);
            case SessionTypes.Mip:
                return await legacyClient.getMipAnalysisSets(legacyQuery);
            case SessionTypes.Mep:
                return await legacyClient.getMepAnalysisSets(legacyQuery);
            case SessionTypes.Snip:
                return await legacyClient.getSnipAnalysisSets(legacyQuery);
            case SessionTypes.Ecg:
                return mapToLegacy(await client.client.getEcgAnalysisSets(undefined,
                    `type eq 'EAS_OVERREAD' and (status eq 'ORS_ACCEPTED' or status eq 'ORS_REJECTED') and modified gt ${moment(startDateIso).utc().toISOString()} and modified le ${moment(endDateIso).utc().toISOString()}`,
                    undefined,
                    `session($select=id,occurred,name,visit;$expand=visit($select=name,subject;$expand=subject($select=site,number;$expand=site($select=id,study,number;$expand=study($select=id,name)))))`
                ));
            case SessionTypes.Oscillometry:
                return await legacyClient.getOscillometryAnalysisSets(legacyQuery);
            case SessionTypes.Pef:
                return mapToLegacy(await client.client.getPefAnalysisSets(undefined,
                    `type eq 'SAS_OVERREAD' and (status eq 'ORS_ACCEPTED' or status eq 'ORS_REJECTED') and modified gt ${moment(startDateIso).utc().toISOString()} and modified le ${moment(endDateIso).utc().toISOString()}`,
                    undefined,
                    `session($select=id,occurred,name,visit;$expand=visit($select=name,subject;$expand=subject($select=site,number;$expand=site($select=id,study,number;$expand=study($select=id,name)))))`
                ));
        }
    }

    const getAnalysts = async () => await legacyClient.getUsersByPermission(study.id, OverreadPermissionsMap()[sessionType], true);

    const OverreadPermissionsMap = (): Dictionary<PermissionEnum> => {
        const permissionMap: Dictionary<PermissionEnum> = {};

        permissionMap[SessionTypes.Fvc] = PermissionEnum.OverreadFvcSession;
        permissionMap[SessionTypes.Svc] = PermissionEnum.OverreadSvcSession;
        permissionMap[SessionTypes.Dlco] = PermissionEnum.OverreadDlcoSession;
        permissionMap[SessionTypes.Mip] = PermissionEnum.OverreadMipSession;
        permissionMap[SessionTypes.Mep] = PermissionEnum.OverreadMepSession;
        permissionMap[SessionTypes.Snip] = PermissionEnum.OverreadSnipSession;
        permissionMap[SessionTypes.Oscillometry] = PermissionEnum.OverreadOscillometrySession;
        permissionMap[SessionTypes.Ecg] = PermissionEnum.OverreadECGSession;
        permissionMap[SessionTypes.Pef] = PermissionEnum.OverreadPefSession;

        return permissionMap;
    }

    let analysisSetType: SpirometryAnalysisSetTypeEnum | DlcoAnalysisSetTypeEnum | PressureAnalysisSetTypeEnum | EcgAnalysisSetTypeEnum | OscillometryAnalysisSetTypeEnum;
    switch (sessionType) {
        case SessionTypes.Svc:
            analysisSetType = SpirometryAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Fvc:
            analysisSetType = SpirometryAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Dlco:
            analysisSetType = DlcoAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Mip:
            analysisSetType = PressureAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Mep:
            analysisSetType = PressureAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Snip:
            analysisSetType = PressureAnalysisSetTypeEnum.Overread;
            break
        case SessionTypes.Ecg:
            analysisSetType = EcgAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Oscillometry:
            analysisSetType = OscillometryAnalysisSetTypeEnum.Overread;
            break;
        case SessionTypes.Pef:
            analysisSetType = SpirometryAnalysisSetTypeEnum.Overread;
            break;

    }

    const init = async () => {
        const name = `QC_BATCH_${format(new Date(), 'yyyyMMddHHmmss')}`;
        const start = subMonths(startOfMonth(new Date()), 1);
        const end = endOfMonth(start);
        const analysisBatchQuery = await legacyClient.analysisBatches({
            Filters: [
                { Member: "StudyId", Condition: QueryCondition.Equal, Values: [study.id] },
                { Member: "SessionTypeId", Condition: QueryCondition.Equal, Values: [sessionType] },
            ],
            Order: {
                Member: 'CreatedDateTime',
                Direction: QueryOrderDirection.Descending,
            }
        });
        setAnalysisBatches(analysisBatchQuery.Data);

        const previousBatch = analysisBatchQuery.Data.length > 0
            ? analysisBatchQuery.Data[0].Id
            : null;

        const overreaders = await getAnalysts();
        setAnalysts(overreaders);
        setValues({ batchName: name, startDate: start.toISOString(), endDate: end.toISOString(), previousBatch: previousBatch, selectedAnalysts: [] });

        await refreshInter();
        await refreshIntra();
    }

    const refreshInter = async () => {

        const state = getFormState();
        const { startDate, endDate } = state.values;

        if (!startDate || !endDate) {
            setInterCandidates([]);
            return;
        }

        setLoading(true);

        const startDateObj = new Date(startDate as string);
        const endDateObj = new Date(endDate as string);

        const startDateIso = startOfDay(startDateObj).toISOString();
        const endDateIso = endOfDay(endDateObj).toISOString();

        const result = await getAnalysisSets(startDateIso, endDateIso);

        setInterCandidates(result.Data);
        setLoading(false);
    }

    const refreshIntra = async () => {

        setLoading(true);
        const state = getFormState();
        const { previousBatch } = state.values;
        const result = await getAnalysisSetsByBatch(previousBatch);

        const uniqueResults = result.Data.filter((value, index, self) => {
            return self.findIndex(item => item.SessionId === value.SessionId) === index;
        });

        setIntraCandidates(uniqueResults);
        setLoading(false);
    }

    const createBatch = async () => {

        setSaving(true);
        setError(undefined);
        const state = getFormState();
        const { batchName, selectedAnalysts, endDate, startDate } = state.values;
        var end = moment(endDate);
        var start = moment(startDate);

        if (end.isBefore(start)) {
            setError("The End Date must be after the Start Date.");
            setSaving(false);
            return;
        }

        if (interSelected.length < 1) {
            setError("There must be at least one session within the date range.");
            setSaving(false);
            return;
        }

        if (!selectedAnalysts) {
            setError("There must be at least one analyst selected.");
            setSaving(false);
            return;
        }

        const includedSessions = interCandidates.concat(intraCandidates).map(s => s.SessionId);
        const selectedSessions = interSelected.concat(intraSelected).map(s => s.SessionId);

        try {
            const result = await legacyClient.createAnalysisBatch({
                name: batchName as string,
                includedSessions: includedSessions,
                selectedSessions: selectedSessions,
                analysts: (selectedAnalysts as any).map(x => x.Id) as string[],
            });
            result && result.Id ? onSave(true) : onSave(false);
        }
        catch (ex) {
            setError(ex.Message);
            setSaving(false);
            return;
        }

        onClose();
    }

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        const sampleSize = SampleSizeLookup(interCandidates.length);
        const samples = _.sampleSize(interCandidates, sampleSize);
        setInterSelected(samples);
    }, [interCandidates]);

    useEffect(() => {
        const sampleSize = SampleSizeLookup(intraCandidates.length);
        const samples = _.sampleSize(intraCandidates, sampleSize);
        setIntraSelected(samples);
    }, [intraCandidates]);

    const handlePreviousBatchesToggle = () => setPreviousBatchesVisible(!previousBatchesVisible);

    const handleCurrentBatchToggle = () => setCurrentBatchVisible(!currentBatchVisible);

    const handlePreviousBatchToggle = () => setPreviousBatchVisible(!previousBatchVisible);

    const handleInterToggle = () => setInterVisible(!interVisible);

    const handleIntraToggle = () => setIntraVisible(!intraVisible);

    const batchOptions = analysisBatches.map(o => (
        <option key={o.Id} value={o.Id}>{o.Name}</option>
    ));

    // HACK: Just hide the form so that the state is kept.
    const contentVisible = previousBatchesVisible || currentBatchVisible || previousBatchVisible || interVisible || intraVisible
        ? 'none'
        : 'block';

    return (
        <>
            <Modal header="Create QC Batch" show={true} size={ModalSize.Normal}>
                <div style={{ display: contentVisible }}>
                    <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>{error}</Alert>
                    <div className="modal-body">
                        <div className="row">
                            <div className="form-group col-md-12">
                                <label htmlFor="batchName">Batch Name</label>
                                <TextBox name="batchName" id="batchName" field="batchName" disabled />
                            </div>

                            <div className="form-group col-md-6">
                                <label htmlFor="startDate">Start Date</label>
                                <DateTime name="startDate" id="startDate" field="startDate" dateFormat="dd-MMM-yyyy" placeholderText="DD-MMM-YYYY" disabled={loading || saving} onChange={refreshInter} />
                            </div>

                            <div className="form-group col-md-6">
                                <label htmlFor="endDate">End Date</label>
                                <DateTime name="endDate" id="endDate" field="endDate" dateFormat="dd-MMM-yyyy" placeholderText="DD-MMM-YYYY" disabled={loading || saving} onChange={refreshInter} />
                            </div>

                            <div className="form-group col-md-12">
                                <label htmlFor="previousBatch">Previous Batch</label>
                                <Dropdown name="previousBatch" id="previousBatch" field="previousBatch" disabled>{batchOptions}</Dropdown>
                            </div>

                            <div className="form-group col-md-12">
                                <button className="btn btn-link" disabled={loading || saving} onClick={handlePreviousBatchesToggle}>View all previous batches</button>
                            </div>

                            <div className="form-group col-md-12">
                                <label htmlFor="analysts">Analysts</label>
                                <MultiSelect name="selectedAnalysts" id="analysts" field="selectedAnalysts" valueKey="Id" labelKey="Username" options={analysts} disabled={loading || saving} multi={true} />
                            </div>

                            <div className="form-group col-md-12" style={{ paddingTop: "40px" }}>
                                {loading ? <LoadingWithMessage message="Preparing analysis batch" /> : null}
                                {saving ? <LoadingWithMessage message="Creating analysis batch" /> : null}
                                {loading || saving ? null : <table className="table">
                                    <tbody>
                                        <tr>
                                            <th>Current Batch</th>
                                            <td data-automationid="currentBatchRowCount">{interCandidates.length}</td>
                                            <td className="text-right"><button className="btn btn-link" data-automationid="viewCurrentBatch" onClick={handleCurrentBatchToggle}>View sessions</button></td>
                                        </tr>
                                        <tr>
                                            <th>Previous Batch</th>
                                            <td data-automationid="previousBatchRowCount">{intraCandidates.length}</td>
                                            <td className="text-right"><button className="btn btn-link" onClick={handlePreviousBatchToggle}>View sessions</button></td>
                                        </tr>
                                        <tr>
                                            <th>Inter-variability</th>
                                            <td data-automationid="intervariabilityRowCount">{interSelected.length}</td>
                                            <td className="text-right"><button className="btn btn-link" onClick={handleInterToggle}>View sessions</button></td>
                                        </tr>
                                        <tr>
                                            <th>Intra-variability</th>
                                            <td data-automationid="intravariabilityRowCount">{intraSelected.length}</td>
                                            <td className="text-right"><button className="btn btn-link" onClick={handleIntraToggle}>View sessions</button></td>
                                        </tr>
                                    </tbody>
                                </table>}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-primary" disabled={loading || saving} onClick={createBatch} data-automationid="QCBatchCreateButton">Create</button>
                        <button className="btn btn-primary btn-outline" onClick={onClose} data-automationid="QCBatchCancelButton">Cancel</button>
                    </div>
                </div>
                {previousBatchesVisible ? <AnalysisBatchesTable analysisBatches={analysisBatches} title="Previous Batches" currentUser={currentUser} onClose={handlePreviousBatchesToggle} onDelete={init} /> : null}
                {currentBatchVisible ? <AnalysisSetTable analysisSets={interCandidates} title="Current Sessions" onClose={handleCurrentBatchToggle} /> : null}
                {previousBatchVisible ? <AnalysisSetTable analysisSets={intraCandidates} title="Previous Sessions" onClose={handlePreviousBatchToggle} /> : null}
                {interVisible ? <AnalysisSetTable analysisSets={interSelected} title="Inter-variability Sessions" onClose={handleInterToggle} /> : null}
                {intraVisible ? <AnalysisSetTable analysisSets={intraSelected} title="Intra-variability Sessions" onClose={handleIntraToggle} /> : null}
            </Modal>
        </>
    );
});

interface CreateAnalysisBatchModalProps {
    currentUser: User;
    sessionType: SessionTypes;
    minimumTests: number;
    onSave: (result: boolean) => void;
    onClose: () => void;
}

export const CreateAnalysisBatchModal = ({ currentUser, sessionType, minimumTests, onClose, onSave }: CreateAnalysisBatchModalProps) => (
    <Form validateOn="submit">
        <CreateAnalysisBatchModalForm currentUser={currentUser} sessionType={sessionType} minimumTests={minimumTests} onClose={onClose} onSave={onSave} />
    </Form>
);
